import { joiResolver } from "@hookform/resolvers/joi";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { animateScroll as scroll } from "react-scroll";
import RatingItemCleaner from "../../../../components/RatingItemCleaner/RatingItemCleaner";
import CustomDropdown from "../../../../components/UI/CustomDropdown/CustomDropdown";
import logo from '../../../../constants/images/logo.svg';
import background from '../../../../constants/images/registration-step-2-icon.svg';
import { citiesService } from "../../../../services/cities.service";
import { registrationCleanerActions } from "../../../../store/slices/registrationCleanerSlice";
import { registrationCleanerStep2Validator } from "../../../../validators/registrationCleanerStep2.validator";
import styles from './RegistrationPageCleanerStep2.module.scss';

const RegistrationPageCleanerStep2 = ({ setCurrentStep }) => {
  const dispatch = useDispatch();
  const {
    city,
    has_experience,
    years_of_experience,
    prevPlaceWork,
    is_punctual,
    is_stress_resistant,
    speed_of_cleaning,
    quality_of_cleaning,
    teamwork,
    has_own_equipment
  } = useSelector(state => state.registrationCleaner);

  // Active (serviceable) cities are managed by the admin and fetched from the
  // public /cities/ endpoint. We use the city `name` as the dropdown value to
  // stay compatible with how the backend stores `city` as text.
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(true);

  useEffect(() => {
    let active = true;

    const fetchCities = async () => {
      try {
        const { data } = await citiesService.getActive();
        const list = Array.isArray(data) ? data : (data?.results || []);
        const names = list.map(item => item?.name).filter(Boolean);
        if (active) setCities(names);
      } catch (error) {
        console.error('Failed to load serviceable cities:', error);
        if (active) setCities([]);
      } finally {
        if (active) setIsLoadingCities(false);
      }
    };

    fetchCities();

    return () => {
      active = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError
  } = useForm({
    resolver: joiResolver(registrationCleanerStep2Validator),
    mode: 'all',
    defaultValues: {
      has_experience,
      years_of_experience,
      prevPlaceWork,
    }
  });

  const handleChangePunctualityRating = useCallback((rating) => {
    dispatch(registrationCleanerActions.setPunctualityRating(rating));
  }, [dispatch]);

  const handleChangeStressResistanceRating = useCallback((rating) => {
    dispatch(registrationCleanerActions.setStressResistanceRating(rating));
  }, [dispatch]);

  const handleChangeCleaningSpeedRating = useCallback((rating) => {
    dispatch(registrationCleanerActions.setCleaningSpeedRating(rating));
  }, [dispatch]);

  const handleChangeQualityCleaningRating = useCallback((rating) => {
    dispatch(registrationCleanerActions.setQualityCleaningRating(rating));
  }, [dispatch]);

  const handleChangeWillingnessWorkInTeam = useCallback((res) => {
    dispatch(registrationCleanerActions.setWillingnessWorkInTeam(res));
  }, [dispatch]);

  const handleChangeHavingCleaningSupplies = useCallback((rating) => {
    dispatch(registrationCleanerActions.setHavingCleaningSupplies(rating));
  }, [dispatch]);

  const ratingItemDescription = useMemo(() => [
    {
      title: 'Пунктуальність',
      handleChangeData: handleChangePunctualityRating,
      ratingFeature: is_punctual,
    },
    {
      title: 'Комунікабельність',
      handleChangeData: handleChangeStressResistanceRating,
      ratingFeature: is_stress_resistant,
    },
    {
      title: 'Швидкість роботи',
      handleChangeData: handleChangeCleaningSpeedRating,
      ratingFeature: speed_of_cleaning,
    },
    {
      title: 'Якість прибирання',
      handleChangeData: handleChangeQualityCleaningRating,
      ratingFeature: quality_of_cleaning,
    },
  ], [
    handleChangePunctualityRating,
    handleChangeStressResistanceRating,
    handleChangeCleaningSpeedRating,
    handleChangeQualityCleaningRating,
    is_punctual,
    is_stress_resistant,
    speed_of_cleaning,
    quality_of_cleaning
  ]);

  const onSubmit = useCallback((data) => {
    const { has_experience, prevPlaceWork, years_of_experience } = data;

    dispatch(registrationCleanerActions.setYearsOfExperience(years_of_experience));
    dispatch(registrationCleanerActions.setPrevPlaceWork(prevPlaceWork));
    dispatch(registrationCleanerActions.setHasExperience(has_experience));

    setCurrentStep(prev => prev + 1);
  }, [dispatch, setCurrentStep]);

  const onError = useCallback(() => {
    if (!city) {
      const cityDropdown = document.getElementById("cleaner_city");
      if (cityDropdown) {
        cityDropdown.scrollIntoView({ behavior: 'smooth', block: "center" });
        const button = cityDropdown.querySelector("div");
        button.style.borderColor = "#F04438";

        let errorMessageSpan = cityDropdown.querySelector(":scope > span");
        if (!errorMessageSpan) {
          errorMessageSpan = document.createElement("span");
          errorMessageSpan.textContent = "Заповніть це поле";
          errorMessageSpan.style.color = "#F04438";
          errorMessageSpan.style.fontSize = "14px";
          errorMessageSpan.style.fontWeight = "500";
          errorMessageSpan.style.lineHeight = "20px";
          cityDropdown.appendChild(errorMessageSpan);
        }
      }
    }

    scroll.scrollToTop();
  }, [city]);

  const handleChangeHasExperience = useCallback((event) => {
    const hasExperience = event.target.value;
    dispatch(registrationCleanerActions.setHasExperience(hasExperience));
  }, [dispatch]);

  const handleChangeYearsOfExperience = useCallback((event) => {
    const yearsOfExperience = event.target.value;
    dispatch(registrationCleanerActions.setYearsOfExperience(yearsOfExperience));
  }, [dispatch]);

  const handleChangePlaceOfWork = useCallback((event) => {
    const placeOfWork = event.target.value;
    dispatch(registrationCleanerActions.setPrevPlaceWork(placeOfWork));
  }, [dispatch]);

  useEffect(() => {
    const cityDropdown = document.getElementById("cleaner_city");
    if (cityDropdown) {
      let errorMessageSpan = cityDropdown.querySelector(":scope > span");
      if (errorMessageSpan) {
        cityDropdown.removeChild(errorMessageSpan);
      }
      const button = cityDropdown.querySelector("div");
      button.style.borderColor = "rgba(231, 232, 233, 1)";
    }
  }, [city]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  return (
    <div className={styles.registrationPageCleanerStep2}>
      <div className={styles.registrationPageCleanerStep2_registration}>
        <div className={styles.registrationPageCleanerStep2_registration_logo}>
          <img src={logo} alt="logo icon" />
        </div>

        <h2 className={styles.registrationPageCleanerStep2_registration_title}>
          <span>2/3 </span> Реєстрація
        </h2>

        <form
          autoComplete='off'
          onSubmit={handleSubmit(onSubmit, onError)}
          className={styles.registrationPageCleanerStep2_registration_form}
        >
          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Місто для роботи <sup>*</sup></span>
              <CustomDropdown
                id={"cleaner_city"}
                type={'cityForm'}
                defaultOption={isLoadingCities ? 'Завантаження міст...' : 'Виберіть місто'}
                data={cities}
                saveData={(city) => dispatch(registrationCleanerActions.setCity(city))}
              />
            </label>
          </div>

          <div className={styles.registrationPageCleanerStep2_registration_form_details}>
            <div className={`${styles.labelContainer} ${styles.prevPlaceWork}`}>
              <label className={styles.label}>
                <span>Досвід в клінінгу <sup>*</sup></span>
                <input
                  {...register('has_experience')}
                  onChange={handleChangeHasExperience}
                  className={styles.input}
                  type="text"
                  placeholder={'Чи маєте досвід у клінінгу?'}
                  style={{
                    borderColor: errors?.has_experience && "#F04438"
                  }}
                  autoComplete="new_has_experience"
                />
              </label>
              {errors.has_experience &&
                <span className={styles.error}> {errors.has_experience.message}</span>}
            </div>

            <div className={`${styles.labelContainer} ${styles.yearsExperience}`}>
              <label className={styles.label}>
                <span>Досвід роботи (роки) <sup>*</sup></span>
                <input
                  {...register('years_of_experience')}
                  onChange={handleChangeYearsOfExperience}
                  className={styles.input}
                  type="number"
                  placeholder={'Введіть кількість років досвіду'}
                  min={0}
                  style={{
                    borderColor: errors?.years_of_experience && "#F04438"
                  }}
                  autoComplete="new_years_of_experience"
                />
              </label>
              {errors.years_of_experience &&
                <span className={styles.error}> {errors.years_of_experience.message}</span>}
            </div>
          </div>

          <div className={styles.labelContainer}>
            <label className={styles.label}>
              <span>Попереднє місце роботи <sup>*</sup></span>
              <input
                {...register('prevPlaceWork')}
                onChange={handleChangePlaceOfWork}
                className={styles.input}
                type="text"
                placeholder={'Причина зміни роботи'}
                style={{
                  borderColor: errors?.prevPlaceWork && "#F04438"
                }}
                autoComplete="new_prevPlaceWork"
              />
            </label>
            {errors.prevPlaceWork &&
              <span className={styles.error}> {errors.prevPlaceWork.message}</span>}
          </div>

          <div className={styles.registrationPageCleanerStep2_registration_form_rates}>
            <h3>Оцініть себе за шкалою від 1-5</h3>
            <p>1 - не відповідає вам</p>
            <p>5 - повністю вам відповідає </p>
          </div>

          <div className={styles.registrationPageCleanerStep2_registration_form_rating}>
            {ratingItemDescription.map((item, index) => (
              <RatingItemCleaner
                key={index}
                title={item.title}
                ratingFeature={item.ratingFeature}
                handleChangeData={item.handleChangeData}
              />
            ))}

            <div className={styles.rating_item}>
              <p>Робота в команді <sup>*</sup></p>

              <div className={styles.rating_item_buttons}>
                <button
                  type={'button'}
                  onClick={() => handleChangeWillingnessWorkInTeam('Так')}
                  className={`${styles.button} ${teamwork === 'Так' ? styles.active : ''}`}
                >
                  Так
                </button>

                <button
                  type={'button'}
                  onClick={() => handleChangeWillingnessWorkInTeam('Ні')}
                  className={`${styles.button} ${teamwork === 'Ні' ? styles.active : ''}`}
                >
                  Ні
                </button>
              </div>
            </div>

            <div className={styles.rating_item}>
              <p>Наявність інвентарю? <sup>*</sup></p>

              <div className={styles.rating_item_buttons}>
                <button
                  type={'button'}
                  onClick={() => handleChangeHavingCleaningSupplies('Так')}
                  className={`${styles.button} ${has_own_equipment === 'Так' ? styles.active : ''}`}
                >
                  Так
                </button>

                <button
                  type={'button'}
                  onClick={() => handleChangeHavingCleaningSupplies('Ні')}
                  className={`${styles.button} ${has_own_equipment === 'Ні' ? styles.active : ''}`}
                >
                  Ні
                </button>
              </div>
            </div>
          </div>

          <div className={styles.registrationPageCleanerStep2_registration_form_buttons}>
            <button
              type={'button'}
              onClick={() => setCurrentStep(prev => prev - 1)}
              className={styles.prev}
            >Назад
            </button>
            <button
              type={'submit'}
              className={styles.next}
            >Продовжити
            </button>
          </div>
        </form>
      </div>

      <div className={styles.registrationPageCleanerStep2_icon}>
        <img src={background} alt="cleaning process" />
      </div>
    </div>
  );
};

export default RegistrationPageCleanerStep2;
